import { htmlEscape } from './escape';

export interface ISafeHtmlBuilder {
  addText(s: string): ISafeHtmlBuilder;
  addBoldText(s: string): ISafeHtmlBuilder;
  addItalicText(s: string): ISafeHtmlBuilder;
  addStrongText(s: string): ISafeHtmlBuilder;
  addBoldMonospaceText(s: string): ISafeHtmlBuilder;
  addSmallText(s: string): ISafeHtmlBuilder;
  addLineBreakAndAppendText(s: string): ISafeHtmlBuilder;
  addUnorderedList(items: string[]): ISafeHtmlBuilder;
  addUnorderedListWithBoldText(items: string[]): ISafeHtmlBuilder;
  addUnorderedListWithBoldPrefix(items: Array<{ bold: string; rest: string }>): ISafeHtmlBuilder;
  addOrderedList(items: string[]): ISafeHtmlBuilder;
  addDiv(content: (builder: ISafeHtmlBuilder) => void): ISafeHtmlBuilder;
  getSafeHtml(): string;
}

/**
 * SafeHtmlBuilder is a utility class for constructing safe HTML strings by escaping
 * potentially harmful characters. It provides methods to build various HTML structures
 * while ensuring proper escaping to prevent XSS vulnerabilities.
 *
 * Note: This class does not support nested tags like <b><small>text</small></b>.
 * If nested tags are required, consider creating separate components or adjusting the
 * design accordingly.
 */
export class SafeHtmlBuilder implements ISafeHtmlBuilder {

  private safeHtml = '';

  constructor(s?: string) {
    if (s) {
      this.addText(s);
    }
  }

  addText(s: string) {
    this.safeHtml += htmlEscape(s);

    return this;
  }

  addBoldText(s: string) {
    this.safeHtml += '<b>';
    this.addText(s);
    this.safeHtml += '</b>';

    return this;
  }

  addItalicText(s: string) {
    this.safeHtml += '<i>';
    this.addText(s);
    this.safeHtml += '</i>';

    return this;
  }

  addStyledText(style: { bold?: boolean, italic?: boolean, underline?: boolean }, s: string) {
    const { bold, italic, underline } = style;
    if (bold) {
      this.safeHtml += '<b>';
    }
    if (italic) {
      this.safeHtml += '<i>';
    }
    if (underline) {
      this.safeHtml += '<u>';
    }
    this.addText(s);
    if (underline) {
      this.safeHtml += '</u>';
    }
    if (italic) {
      this.safeHtml += '</i>';
    }
    if (bold) {
      this.safeHtml += '</b>';
    }
  }

  buildTree(f: SafeHtmlTreeBuilderFunction) {
    f(new SafeHtmlTreeBuilder((s: string) => this.safeHtml += s));
  }

  addStrongText(s: string) {
    this.safeHtml += '<strong>';
    this.addText(s);
    this.safeHtml += '</strong>';

    return this;
  }

  addBoldMonospaceText(s: string) {
    // Alternative to the deprecated <tt> element in HTML5.
    this.safeHtml += '<b class="font-family: monospace">';
    this.addText(s);
    this.safeHtml += '</b>';

    return this;
  }

  addSmallText(s: string) {
    this.safeHtml += '<small>';
    this.addText(s);
    this.safeHtml += '</small>';

    return this;
  }

  /**
   * @deprecated Use addLineBreak instead.
   */
  addLineBreakAndAppendText(s: string) {
    this.safeHtml += '<br>';
    this.addText(s);

    return this;
  }

  addLineBreaks(count = 1) {
    for (let i = 1; i <= count; i++) {
      this.safeHtml += '<br>';
    }

    return this;
  }

  addLink(url: string, text: string, target = '_blank') {
    this.safeHtml += `<a href="${url}" target="${target}">${htmlEscape(text)}</a>`;
    return this;
  }

  addUnorderedList(items: string[]) {
    this.safeHtml += '<ul>';
    items.forEach(item => {
      this.safeHtml += `<li>${htmlEscape(item)}</li>`;
    });
    this.safeHtml += '</ul>';

    return this;
  }

  addUnorderedListWithBoldText(items: string[]) {
    this.safeHtml += '<ul>';
    items.forEach(item => {
      this.safeHtml += `<li><b>${htmlEscape(item)}</b></li>`;
    });
    this.safeHtml += '</ul>';

    return this;
  }

  addUnorderedListWithBoldPrefix(items: Array<{ bold: string; rest: string }>) {
    this.safeHtml += '<ul>';
    items.forEach(({ bold, rest }) => {
      this.safeHtml += `<li><strong>${htmlEscape(bold)}</strong>${htmlEscape(rest)}</li>`;
    });
    this.safeHtml += '</ul>';

    return this;
  }

  addOrderedList(items: string[]) {
    this.safeHtml += '<ol>';
    items.forEach(item => {
      this.safeHtml += `<li>${htmlEscape(item)}</li>`;
    });
    this.safeHtml += '</ol>';

    return this;
  }

  addDiv(content: (builder: ISafeHtmlBuilder) => void) {
    this.safeHtml += '<div>';
    content(this);
    this.safeHtml += '</div>';

    return this;
  }

  getSafeHtml() {
    return this.safeHtml;
  }

  isEmpty() {
    return this.safeHtml === '';
  }
}

export const isInstanceOfSafeHtml = (value: any): value is SafeHtmlBuilder => {
  return value instanceof SafeHtmlBuilder;
};

export interface Inner {
  add(value: string): void;
}

export class SafeHtmlTreeBuilder {
  constructor(private add: (s: string) => void) { }
  public bold(f: SafeHtmlTreeBuilderFunction): void {
    this.wrappingTag('b', f);
  }
  public italics(f: SafeHtmlTreeBuilderFunction): void {
    this.wrappingTag('i', f);
  }
  public underline(f: SafeHtmlTreeBuilderFunction): void {
    this.wrappingTag('u', f);
  }
  public text(text: string): void {
    this.add(htmlEscape(text));
  }
  private wrappingTag(tag: string, f: SafeHtmlTreeBuilderFunction): void {
    this.add(`<${tag}>`);
    f(this);
    this.add(`</${tag}>`);
  }
}

export type SafeHtmlTreeBuilderFunction = (builder: SafeHtmlTreeBuilder) => void;
